import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import type { SystemHealthData, K8sClusterInfo, NamespaceInfo } from '../types';
import { getHealthColor, getHealthLevel } from '../types/systemHealth';
import { useTheme } from '../hooks/useTheme';

interface HealthHeatmapProps {
  data: SystemHealthData[];
  onSystemClick?: (system: SystemHealthData) => void;
}

export const HealthHeatmap: React.FC<HealthHeatmapProps> = ({ data, onSystemClick }) => {
  const { theme } = useTheme();
  const isDark = theme.name === 'dark';
  const gridColumns = Math.max(1, Math.ceil(Math.sqrt(data.length)));
  const tileBorder = isDark ? 'rgba(15, 23, 42, 0.42)' : 'rgba(255, 255, 255, 0.76)';
  const shadow = isDark ? '0 14px 34px rgba(0, 0, 0, 0.32)' : '0 14px 34px rgba(15, 23, 42, 0.12)';

  const renderSystemBlock = (system: SystemHealthData) => {
    const color = getHealthColor(system.health_percent);
    const level = getHealthLevel(system.health_percent);

    const tooltipContent = (
      <div>
        <div><strong>{system.system_name}</strong></div>
        <div>Code: {system.system_code}</div>
        <div>Type: {system.system_type}</div>
        <div>Status: {system.status}</div>
        <div>Health: {system.health_percent}%</div>
        <div>Pods: {system.running_pods}/{system.total_pods} running</div>
        {system.failed_pods > 0 && <div style={{ color: '#f5222d' }}>Failed: {system.failed_pods}</div>}
        {system.pending_pods > 0 && <div style={{ color: '#fadb14' }}>Pending: {system.pending_pods}</div>}
        {system.clusters && system.clusters.length > 0 && (
          <div>Clusters: {system.clusters.map((c: K8sClusterInfo | string) =>
            typeof c === 'string' ? c : c.name
          ).join(', ')}</div>
        )}
        {system.namespaces && system.namespaces.length > 0 && (
          <div>Namespaces: {system.namespaces.map((ns: NamespaceInfo | string) =>
            typeof ns === 'string' ? ns : ns.name
          ).join(', ')}</div>
        )}
        {system.last_sync_time && <div>Last sync: {new Date(system.last_sync_time).toLocaleString()}</div>}
      </div>
    );

    return (
      <Tooltip key={system.system_id} title={tooltipContent} placement="top" arrow>
        <Box
          onClick={() => onSystemClick?.(system)}
          sx={{
            backgroundColor: color,
            width: '100%',
            aspectRatio: '1',
            p: 1.5,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            textAlign: 'center',
            borderRadius: '8px',
            border: `1px solid ${tileBorder}`,
            boxShadow: isDark ? 'inset 0 0 0 1px rgba(255,255,255,0.05)' : 'inset 0 0 0 1px rgba(255,255,255,0.32)',
            backgroundImage: level === 'unknown'
              ? 'linear-gradient(145deg, rgba(255,255,255,0.42), rgba(148,163,184,0.16))'
              : 'linear-gradient(145deg, rgba(255,255,255,0.2), rgba(0,0,0,0.08))',
            transition: 'transform .18s ease, box-shadow .18s ease, filter .18s ease, border-color .18s ease',
            overflow: 'hidden',
            position: 'relative',
            '&:hover': {
              transform: 'translateY(-3px) scale(1.015)',
              boxShadow: shadow,
              filter: 'saturate(1.08) brightness(1.04)',
              zIndex: 10,
              borderColor: isDark ? 'rgba(255,255,255,0.34)' : 'rgba(255,255,255,0.92)',
            },
          }}
        >
          <Typography
            sx={{
              color: level === 'unknown' ? '#595959' : '#fff',
              fontSize: 13,
              fontWeight: 800,
              wordBreak: 'break-word',
              textShadow: level === 'unknown' ? 'none' : '0 1px 2px rgba(0,0,0,0.3)',
              lineHeight: '1.2',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {system.system_name}
          </Typography>
          <div>
            <div
              style={{
                fontSize: '22px',
                fontWeight: 'bold',
                color: level === 'unknown' ? '#595959' : '#fff',
                textShadow: level === 'unknown' ? 'none' : '0 1px 2px rgba(0,0,0,0.3)',
              }}
            >
              {system.health_percent}%
            </div>
            <Typography
              sx={{
                fontSize: '11px',
                color: level === 'unknown' ? '#8c8c8c' : 'rgba(255,255,255,0.9)',
                lineHeight: 1.2,
              }}
            >
              {system.running_pods}/{system.total_pods} pods
            </Typography>
          </div>
          <Chip
            label={system.status}
            size="small"
            sx={{
              backgroundColor: level === 'unknown' ? undefined : 'rgba(255,255,255,0.2)',
              color: level === 'unknown' ? '#64748b' : '#fff',
              border: 'none',
              fontSize: '10px',
              height: 22,
              borderRadius: '8px',
              margin: 0,
            }}
          />
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(auto-fit, minmax(116px, 1fr))',
          md: `repeat(${gridColumns}, minmax(124px, 1fr))`,
        },
        gap: 1.25,
        width: '100%',
        maxWidth: `${gridColumns * 156}px`,
        margin: '0 auto',
      }}
    >
      {data.map(renderSystemBlock)}
    </Box>
  );
};
